import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Flame, Heart } from "lucide-react";
import MiniGridLoader from '@/components/MiniGridLoader';
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { hapticReaction } from "@/utils/haptics";

interface GraphReactionsProps {
  graphOwnerId: string;
  currentUserId?: string;
  metricKey: string;
  readOnly?: boolean; // Show counts only, no interaction
}

interface Reaction {
  reaction_type: "fire" | "heart";
  count: number;
  userReacted: boolean;
}

export const GraphReactions = ({
 graphOwnerId, currentUserId, metricKey, readOnly = false }: GraphReactionsProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isToggling, setIsToggling] = useState<"fire" | "heart" | null>(null);

  // Fetch reaction counts and user's reactions
  const { data: reactions, isLoading } = useQuery({
    queryKey: ["graph-reactions", graphOwnerId, metricKey, currentUserId],
    queryFn: async () => {
      // Get all reactions for this specific metric graph
      const { data: allReactions, error } = await supabase
        .from("graph_reactions")
        .select("reaction_type, user_id")
        .eq("graph_owner_id", graphOwnerId)
        .eq("metric_key", metricKey);

      if (error) throw error;

      // Count reactions by type and check if current user reacted
      const fireCount = allReactions?.filter(r => r.reaction_type === "fire").length || 0;
      const heartCount = allReactions?.filter(r => r.reaction_type === "heart").length || 0;
      const userFireReaction = currentUserId ? allReactions?.some(r => r.reaction_type === "fire" && r.user_id === currentUserId) : false;
      const userHeartReaction = currentUserId ? allReactions?.some(r => r.reaction_type === "heart" && r.user_id === currentUserId) : false;

      return [
        { reaction_type: "fire" as const, count: fireCount, userReacted: userFireReaction },
        { reaction_type: "heart" as const, count: heartCount, userReacted: userHeartReaction },
      ];
    },
    enabled: !!graphOwnerId && !!metricKey,
  });

  // Toggle reaction mutation
  const toggleReaction = useMutation({
    mutationFn: async (reactionType: "fire" | "heart") => {
      if (!currentUserId) {
        throw new Error("Must be logged in to react");
      }

      // Check if user already has this reaction
      const { data: existing, error: checkError } = await supabase
        .from("graph_reactions")
        .select("id")
        .eq("user_id", currentUserId)
        .eq("graph_owner_id", graphOwnerId)
        .eq("metric_key", metricKey)
        .eq("reaction_type", reactionType)
        .maybeSingle();

      if (checkError) throw checkError;

      if (existing) {
        // Remove reaction
        const { error: deleteError } = await supabase
          .from("graph_reactions")
          .delete()
          .eq("id", existing.id);

        if (deleteError) throw deleteError;
        return { action: "removed", reactionType };
      } else {
        // Add reaction
        const { error: insertError } = await supabase
          .from("graph_reactions")
          .insert({
            user_id: currentUserId,
            graph_owner_id: graphOwnerId,
            metric_key: metricKey,
            reaction_type: reactionType,
          });

        if (insertError) throw insertError;

        // Send push notification (only if not reacting to own graph)
        if (currentUserId !== graphOwnerId) {
          try {
            await supabase.functions.invoke("send-reaction-notification", {
              body: {
                graphOwnerId,
                reactorId: currentUserId,
                metricKey,
                reactionType,
              },
            });
          } catch (notifError) {
            console.error("Failed to send notification:", notifError);
            // Don't fail the reaction if notification fails
          }
        }

        return { action: "added", reactionType };
      }
    },
    onMutate: async (reactionType) => {
      setIsToggling(reactionType);
    },
    onSuccess: (result) => {
      const emoji = result.reactionType === "fire" ? "🔥" : "❤️";
      if (result.action === "added") {
        hapticReaction();
        toast.success(`Reacted with ${emoji}`);
      } else {
        toast.success(`Removed ${emoji} reaction`);
      }
      
      // Invalidate queries to refresh counts
      queryClient.invalidateQueries({ queryKey: ["graph-reactions", graphOwnerId, metricKey] });
      queryClient.invalidateQueries({ queryKey: ["reaction-feed"] });
    },
    onError: (error) => {
      console.error("Failed to toggle reaction:", error);
      toast.error(t('notifications.toast.failed_to_update_reaction'));
    },
    onSettled: () => {
      setIsToggling(null);
    },
  });

  if (isLoading || !reactions) {
    return null;
  }

  // In read-only mode (viewing own profile), just show counts if they exist
  // Don't show anything if there are no reactions
  if (readOnly) {
    const fireReaction = reactions.find(r => r.reaction_type === "fire");
    const heartReaction = reactions.find(r => r.reaction_type === "heart");
    
    // Don't show anything if no reactions exist
    if ((!fireReaction || fireReaction.count === 0) && (!heartReaction || heartReaction.count === 0)) {
      return null;
    }

    return (
      <div className="flex items-center gap-2 mt-2">
        {fireReaction && fireReaction.count > 0 && (
          <div className="h-7 px-2 gap-1 flex items-center text-muted-foreground/60">
            <Flame className="w-4 h-4" />
            <span className="text-[10px] font-medium">
              {fireReaction.count}
            </span>
          </div>
        )}
        {heartReaction && heartReaction.count > 0 && (
          <div className="h-7 px-2 gap-1 flex items-center text-muted-foreground/60">
            <Heart className="w-4 h-4" />
            <span className="text-[10px] font-medium">
              {heartReaction.count}
            </span>
          </div>
        )}
      </div>
    );
  }

  // Don't show reactions on own graph (when not in read-only mode)
  if (currentUserId === graphOwnerId) {
    return null;
  }

  const fireReaction = reactions.find(r => r.reaction_type === "fire");
  const heartReaction = reactions.find(r => r.reaction_type === "heart");

  return (
    <div className="flex items-center gap-2 mt-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => toggleReaction.mutate("fire")}
        disabled={!currentUserId || isToggling === "fire"}
        className={cn(
          "h-7 px-2 gap-1 hover:bg-orange-500/10 transition-colors relative",
          fireReaction?.userReacted && "bg-orange-500/20 hover:bg-orange-500/30"
        )}
      >
        {isToggling === "fire" ? (
          <MiniGridLoader className="w-4 h-4 text-muted-foreground" />
        ) : (
          <Flame className={cn(
            "w-4 h-4",
            fireReaction?.userReacted ? "fill-orange-500 text-orange-500" : "text-muted-foreground"
          )} />
        )}
        {fireReaction && fireReaction.count > 0 && (
          <span className="text-[10px] font-medium text-muted-foreground">
            {fireReaction.count}
          </span>
        )}
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => toggleReaction.mutate("heart")}
        disabled={!currentUserId || isToggling === "heart"}
        className={cn(
          "h-7 px-2 gap-1 hover:bg-pink-500/10 transition-colors relative",
          heartReaction?.userReacted && "bg-pink-500/20 hover:bg-pink-500/30"
        )}
      >
        {isToggling === "heart" ? (
          <MiniGridLoader className="w-4 h-4 text-muted-foreground" />
        ) : (
          <Heart className={cn(
            "w-4 h-4",
            heartReaction?.userReacted ? "fill-pink-500 text-pink-500" : "text-muted-foreground"
          )} />
        )}
        {heartReaction && heartReaction.count > 0 && (
          <span className="text-[10px] font-medium text-muted-foreground">
            {heartReaction.count}
          </span>
        )}
      </Button>
    </div>
  );
};
